// ---------------------------------------------------------------------------
// Copy for the "We are hiring" page (/hiring).
// Unlike the rest of the site this page exists in five languages; the page
// has its own language switch, independent of the EN/DE site switch. The
// English text is the master – change it first, then the translations.
// ---------------------------------------------------------------------------
import { Lang } from "config/i18n/languages";
import itCopy from "./locales/it/hiring.json";
import plCopy from "./locales/pl/hiring.json";
import jaCopy from "./locales/ja/hiring.json";
import koCopy from "./locales/ko/hiring.json";

export type HiringLang = Lang | "zh";

/** Order of the in-page language switch; htmlLang feeds the lang attribute. */
export const HIRING_LANGS: {
  code: HiringLang;
  label: string;
  htmlLang: string;
}[] = [
  { code: "en", label: "English", htmlLang: "en" },
  { code: "de", label: "Deutsch", htmlLang: "de" },
  { code: "fr", label: "Français", htmlLang: "fr" },
  { code: "es", label: "Español", htmlLang: "es" },
  { code: "it", label: "Italiano", htmlLang: "it" },
  { code: "pl", label: "Polski", htmlLang: "pl" },
  { code: "zh", label: "中文", htmlLang: "zh-Hans" },
  { code: "ja", label: "日本語", htmlLang: "ja" },
  { code: "ko", label: "한국어", htmlLang: "ko" },
];

/** Applications go to the address already published on the contact page. */
export const CONTACT_MAIL = "contact@marksim.org";

export interface Posting {
  pageTitle: string;
  eyebrow: string;
  title: string;
  /** Short fact chips shown in the hero and in the summary card. */
  facts: string[];
  intro: string[];
  challenge: { title: string; text: string };
  deliverables: { title: string; items: string[] };
  apply: { title: string; text: string; button: string; mailSubject: string };
  /** aria-label of the language switch. */
  langLabel: string;
}

const en: Posting = {
  pageTitle: "We are hiring – NLP Model Development & Testing",
  eyebrow: "We are hiring",
  title: "NLP Model Development & Testing",
  facts: [
    "Skilled NLP collaborator",
    "Open-source tools throughout",
    "CPU-only training, 64 GB RAM",
    "Thousands of documents, in-house",
  ],
  intro: [
    "We are building a production-ready natural language processing model and are looking for a skilled collaborator who can take it from raw data through to a fully tested release. The work covers data preparation, model architecture selection, training, hyper-parameter tuning and a robust evaluation cycle with clear metrics and error analysis.",
    "You will work with common NLP libraries such as TensorFlow, PyTorch, Hugging Face Transformers or spaCy. You should be comfortable moving between them and setting up reproducible experiments.",
  ],
  challenge: {
    title: "The constraint: no GPU",
    text: "During the training phase the model has to run on our standard company servers: 64 GB RAM, no GPU. We want to process thousands of documents and need an in-house solution – the model is trained and run on our own servers.",
  },
  deliverables: {
    title: "Deliverables",
    items: [
      "End-to-end NLP model (source code, training scripts, environment files)",
      "Comprehensive test report with benchmarks, confusion matrices and recommendations",
      "Brief hand-off session walking through the results and next-step optimisations",
      "Everything developed with open-source tools",
    ],
  },
  apply: {
    title: "How to apply",
    text: "When you respond, focus on the relevant NLP projects you have already shipped – especially anything that demonstrates measurable gains in accuracy, latency or scalability. A concise rundown of your practical experience helps us gauge fit quickly. Detailed proposals can wait until we have discussed the dataset and the target deployment environment.",
    button: "Apply by e-mail",
    mailSubject: "Application: NLP Model Development & Testing",
  },
  langLabel: "Language",
};

const de: Posting = {
  pageTitle: "We are hiring – NLP-Modellentwicklung & Testing",
  eyebrow: "We are hiring",
  title: "NLP-Modellentwicklung & Testing",
  facts: [
    "Erfahrene NLP-Fachkraft gesucht",
    "Durchgehend Open-Source-Werkzeuge",
    "Training ohne GPU, 64 GB RAM",
    "Tausende Dokumente, hausintern",
  ],
  intro: [
    "Wir entwickeln ein produktionsreifes Modell zur Verarbeitung natürlicher Sprache (NLP) und suchen eine erfahrene Fachkraft, die es von den Rohdaten bis zum vollständig getesteten Release führt. Die Arbeit umfasst Datenaufbereitung, die Wahl der Modellarchitektur, Training, Hyperparameter-Optimierung sowie einen belastbaren Evaluierungszyklus mit klaren Metriken und Fehleranalyse.",
    "Sie arbeiten mit gängigen NLP-Bibliotheken wie TensorFlow, PyTorch, Hugging Face Transformers oder spaCy. Sie sollten zwischen diesen Bibliotheken sicher wechseln und reproduzierbare Experimente aufsetzen können.",
  ],
  challenge: {
    title: "Die Einschränkung: keine GPU",
    text: "In der Trainingsphase muss das Modell auf unseren Standard-Firmenservern laufen: 64 GB RAM, keine GPU. Wir möchten Tausende Dokumente verarbeiten und brauchen eine hausinterne Lösung – das Modell wird auf unseren eigenen Servern trainiert und betrieben.",
  },
  deliverables: {
    title: "Lieferumfang",
    items: [
      "Vollständiges NLP-Modell (Quellcode, Trainingsskripte, Umgebungsdateien)",
      "Umfassender Testbericht mit Benchmarks, Konfusionsmatrizen und Empfehlungen",
      "Kurze Übergabe, in der wir die Ergebnisse und die nächsten Optimierungsschritte durchgehen",
      "Alles mit Open-Source-Werkzeugen entwickelt",
    ],
  },
  apply: {
    title: "So bewerben Sie sich",
    text: "Konzentrieren Sie sich in Ihrer Antwort auf die relevanten NLP-Projekte, die Sie bereits erfolgreich umgesetzt haben – vor allem auf solche, die messbare Verbesserungen bei Genauigkeit, Latenz oder Skalierbarkeit belegen. Ein knapper Überblick über Ihre praktische Erfahrung hilft uns, rasch einzuschätzen, ob es passt. Detaillierte Konzepte haben Zeit, bis wir den Datensatz und die Zielumgebung für den Einsatz besprochen haben.",
    button: "Per E-Mail bewerben",
    mailSubject: "Bewerbung: NLP-Modellentwicklung & Testing",
  },
  langLabel: "Sprache",
};

const fr: Posting = {
  pageTitle: "We are hiring – Développement et test d’un modèle NLP",
  eyebrow: "We are hiring",
  title: "Développement et test d’un modèle NLP",
  facts: [
    "Collaborateur NLP expérimenté",
    "Outils open source exclusivement",
    "Entraînement sur CPU uniquement, 64 Go de RAM",
    "Des milliers de documents, en interne",
  ],
  intro: [
    "Nous développons un modèle de traitement automatique du langage naturel (NLP) prêt pour la production et recherchons un collaborateur expérimenté, capable de le mener des données brutes jusqu’à une version entièrement testée. Le travail couvre la préparation des données, le choix de l’architecture du modèle, l’entraînement, le réglage des hyperparamètres et un cycle d’évaluation rigoureux, avec des métriques claires et une analyse des erreurs.",
    "Vous travaillerez avec les bibliothèques NLP courantes telles que TensorFlow, PyTorch, Hugging Face Transformers ou spaCy. Vous devrez être à l’aise pour passer de l’une à l’autre et pour mettre en place des expériences reproductibles.",
  ],
  challenge: {
    title: "La contrainte : pas de GPU",
    text: "Pendant la phase d’entraînement, le modèle doit tourner sur nos serveurs d’entreprise standard : 64 Go de RAM, pas de GPU. Nous souhaitons traiter des milliers de documents et avons besoin d’une solution interne – le modèle est entraîné et exécuté sur nos propres serveurs.",
  },
  deliverables: {
    title: "Livrables",
    items: [
      "Modèle NLP de bout en bout (code source, scripts d’entraînement, fichiers d’environnement)",
      "Rapport de test complet avec benchmarks, matrices de confusion et recommandations",
      "Brève session de passation présentant les résultats et les pistes d’optimisation pour la suite",
      "Le tout développé avec des outils open source",
    ],
  },
  apply: {
    title: "Comment postuler",
    text: "Dans votre réponse, concentrez-vous sur les projets NLP pertinents que vous avez déjà livrés – en particulier tout ce qui démontre des gains mesurables en précision, en latence ou en montée en charge. Un aperçu concis de votre expérience pratique nous permet d’évaluer rapidement l’adéquation de votre profil. Les propositions détaillées pourront attendre que nous ayons discuté du jeu de données et de l’environnement de déploiement cible.",
    button: "Postuler par e-mail",
    mailSubject: "Candidature : Développement et test d’un modèle NLP",
  },
  langLabel: "Langue",
};

const es: Posting = {
  pageTitle: "We are hiring – Desarrollo y pruebas de un modelo NLP",
  eyebrow: "We are hiring",
  title: "Desarrollo y pruebas de un modelo NLP",
  facts: [
    "Colaborador experto en NLP",
    "Solo herramientas de código abierto",
    "Entrenamiento solo en CPU, 64 GB RAM",
    "Miles de documentos, solución interna",
  ],
  intro: [
    "Estamos desarrollando un modelo de procesamiento del lenguaje natural (PLN/NLP) listo para producción y buscamos un colaborador con experiencia que lo lleve desde los datos en bruto hasta una versión completamente probada. El trabajo abarca la preparación de los datos, la selección de la arquitectura del modelo, el entrenamiento, el ajuste de hiperparámetros y un ciclo de evaluación robusto con métricas claras y análisis de errores.",
    "Trabajará con bibliotecas de NLP habituales como TensorFlow, PyTorch, Hugging Face Transformers o spaCy. Debe manejarse con soltura entre ellas y ser capaz de configurar experimentos reproducibles.",
  ],
  challenge: {
    title: "La limitación: sin GPU",
    text: "Durante la fase de entrenamiento, el modelo debe ejecutarse en nuestros servidores corporativos estándar: 64 GB de RAM y sin GPU. Queremos procesar miles de documentos y necesitamos una solución interna: el modelo se entrena y se ejecuta en nuestros propios servidores.",
  },
  deliverables: {
    title: "Entregables",
    items: [
      "Modelo NLP de extremo a extremo (código fuente, scripts de entrenamiento, archivos de entorno)",
      "Informe de pruebas completo con benchmarks, matrices de confusión y recomendaciones",
      "Breve sesión de traspaso para revisar los resultados y las optimizaciones a abordar como siguiente paso",
      "Todo desarrollado con herramientas de código abierto",
    ],
  },
  apply: {
    title: "Cómo presentar su candidatura",
    text: "Al responder, destaque los proyectos NLP relevantes que ya haya entregado, en especial aquellos que demuestren mejoras medibles en precisión, latencia o escalabilidad. Un resumen conciso de su experiencia práctica nos ayuda a evaluar rápidamente si su perfil se ajusta a lo que buscamos. Las propuestas detalladas pueden esperar hasta que hayamos hablado del conjunto de datos y del entorno de despliegue previsto.",
    button: "Enviar candidatura",
    mailSubject: "Candidatura: Desarrollo y pruebas de un modelo NLP",
  },
  langLabel: "Idioma",
};

const zh: Posting = {
  pageTitle: "We are hiring – NLP 模型开发与测试",
  eyebrow: "We are hiring",
  title: "NLP 模型开发与测试",
  facts: [
    "经验丰富的 NLP 合作者",
    "全程使用开源工具",
    "仅用 CPU 训练，64 GB RAM",
    "数千份文档，内部部署",
  ],
  intro: [
    "我们正在构建一个可投入生产的自然语言处理（NLP）模型，现寻找一位经验丰富的合作者，能够将模型从原始数据一路推进至经过全面测试的正式发布。工作内容涵盖数据准备、模型架构选择、训练、超参数调优，以及一套包含明确指标与错误分析的严谨评估流程。",
    "您将使用 TensorFlow、PyTorch、Hugging Face Transformers 或 spaCy 等常用 NLP 库。您应能在这些库之间灵活切换，并搭建可复现的实验。",
  ],
  challenge: {
    title: "限制条件：没有 GPU",
    text: "在训练阶段，模型必须在我们的公司标准服务器上运行：64 GB RAM，没有 GPU。我们希望处理数千份文档，因此需要一套内部解决方案——模型在我们自己的服务器上训练和运行。",
  },
  deliverables: {
    title: "交付成果",
    items: [
      "端到端 NLP 模型（源代码、训练脚本、环境配置文件）",
      "完整的测试报告，包含基准测试、混淆矩阵和改进建议",
      "简短的交接会议，讲解结果及后续优化方向",
      "全部使用开源工具开发",
    ],
  },
  apply: {
    title: "申请方式",
    text: "回复时，请重点介绍您已经交付的相关 NLP 项目，尤其是那些在准确率、延迟或可扩展性方面取得了可量化提升的项目。简要概述您的实践经验，有助于我们快速判断是否契合。详细方案可以等到我们讨论过数据集和目标部署环境之后再提供。",
    button: "通过电子邮件申请",
    mailSubject: "申请：NLP 模型开发与测试",
  },
  langLabel: "语言",
};

const hiring: Record<HiringLang, Posting> = {
  en,
  de,
  fr,
  es,
  it: itCopy,
  pl: plCopy,
  ja: jaCopy,
  ko: koCopy,
  zh,
};

export default hiring;
